import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { FaEyeSlash, FaFileAlt, FaLock } from "react-icons/fa";

const PdfReader = React.lazy(() => import("./PdfReader"));
const DocxReader = React.lazy(() => import("./DocxReader"));

// ============================================================================
// Read-only viewing for paid content.
//
// What this actually guarantees, and what it doesn't — worth being straight
// about, because the difference matters:
//
//   * No download. The files aren't reachable by URL any more; they come from a
//     signed, expiring, per-student endpoint that serves them inline only. PDFs
//     are painted to <canvas> by pdf.js and Word docs are converted to HTML in
//     the page, so neither shows a browser "save/print" toolbar.
//   * No copy/print. Selection, right-click, drag, Ctrl/⌘+S, Ctrl/⌘+P and the
//     print stylesheet are all blocked.
//   * Screenshots CANNOT be blocked by a web page — no browser exposes that.
//     What's here instead is deterrence and traceability: the content blurs the
//     moment the tab loses focus (which is what most capture tools need), a
//     PrintScreen press clears the clipboard and warns, and every view carries
//     a visible watermark with the viewer's own email, so a leaked screenshot
//     points straight back at whoever took it.
// ============================================================================

// Who's looking — used for the watermark. Falls back to a generic label if the
// token can't be read, so the overlay never disappears entirely.
export const useViewerIdentity = () => useMemo(() => {
    try {
        const decoded = jwtDecode(localStorage.getItem("token"));
        return { email: decoded?.email || "", id: decoded?.id || null };
    } catch (e) {
        return { email: "", id: null };
    }
}, []);

// Tiled diagonal watermark, drawn as a background image so it can't be selected
// or clicked through to.
const Watermark = ({ label }) => {
    const svg = useMemo(() => {
        const text = String(label || "Teechaa").slice(0, 60)
            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        return `<svg xmlns="http://www.w3.org/2000/svg" width="340" height="200">
            <text x="50%" y="50%" transform="rotate(-30 170 100)" text-anchor="middle"
                font-family="Montserrat, Arial, sans-serif" font-size="15" fill="rgba(17,24,39,0.13)">${text}</text>
        </svg>`;
    }, [label]);

    return (
        <div
            aria-hidden="true"
            className="absolute inset-0 z-20 pointer-events-none select-none"
            style={{ backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`, backgroundRepeat: "repeat" }}
        />
    );
};

// Wraps any paid content. Applies the deterrents above and stamps the viewer's
// email across it.
export const ProtectedArea = ({ children, className = "", contentClassName = "", note = true }) => {
    const { email, id } = useViewerIdentity();
    const [shielded, setShielded] = useState(false);
    const [warned, setWarned] = useState(false);
    const ref = useRef(null);

    const label = `${email || "Teechaa student"}${id ? ` · #${id}` : ""}`;

    useEffect(() => {
        // While a protected area is on screen, the print stylesheet (index.css)
        // blanks the page — so Ctrl+P / "Save as PDF" gets nothing.
        document.body.classList.add("protected-open");
        return () => document.body.classList.remove("protected-open");
    }, []);

    useEffect(() => {
        // Hide the content whenever the tab/window isn't the one in front. Most
        // screen-capture and screen-share tools have to take focus first.
        // Clicking into an embedded viewer also blurs the window, so that case
        // is excluded or the document would shield itself the moment it's used.
        const hide = () => {
            if (document.activeElement?.tagName === "IFRAME") return;
            setShielded(true);
        };
        // Coming back is deliberately generous — the shield is a deterrent, not
        // a lock, and a student must never end up stuck behind it. Any of
        // focus, the tab becoming visible, or a click on the shield itself
        // brings the content back.
        const show = () => setShielded(false);
        const onVisibility = () => { if (document.visibilityState === "visible") setShielded(false); };

        window.addEventListener("focus", show);
        window.addEventListener("blur", hide);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("focus", show);
            window.removeEventListener("blur", hide);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            const mod = e.ctrlKey || e.metaKey;
            // Save / print / "save page as".
            if (mod && ["s", "p"].includes(e.key.toLowerCase())) {
                e.preventDefault();
                setWarned(true);
            }
        };
        const onKeyUp = (e) => {
            if (e.key === "PrintScreen") {
                // Can't stop the capture, but the clipboard copy can be wiped
                // on the platforms where PrintScreen goes there.
                if (navigator.clipboard?.writeText) navigator.clipboard.writeText("").catch(() => {});
                setWarned(true);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);

    useEffect(() => {
        if (!warned) return undefined;
        const t = setTimeout(() => setWarned(false), 4000);
        return () => clearTimeout(t);
    }, [warned]);

    const block = (e) => { e.preventDefault(); return false; };

    return (
        <div
            ref={ref}
            className={`relative select-none ${className}`}
            onContextMenu={block}
            onCopy={block}
            onCut={block}
            onDragStart={block}
            style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
        >
            <div className={`${contentClassName} ${shielded ? "blur-xl pointer-events-none" : ""}`}>{children}</div>
            <Watermark label={label} />

            {shielded && (
                <button
                    type="button"
                    onClick={() => setShielded(false)}
                    className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm text-center p-6 w-full"
                >
                    <FaEyeSlash className="text-gray-400" size={26} />
                    <p className="text-sm font-semibold text-gray-700 mt-2">Content hidden</p>
                    <p className="text-xs text-gray-500 mt-1">Click anywhere to keep reading.</p>
                </button>
            )}

            {warned && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
                    <FaLock size={12} /> This content is protected — saving, printing and copying are disabled.
                </div>
            )}

            {note && (
                <p className="relative z-30 text-[11px] text-gray-400 mt-2 flex items-center gap-1.5">
                    <FaLock size={9} /> Protected content · watermarked to {email || "your account"}
                </p>
            )}
        </div>
    );
};

// Renders one segment attachment in the way that keeps it in-app. `kind` comes
// from the API (attachment_kind); it's re-derived from the URL as a fallback for
// any response that predates it.
//
// `documentUrl` is the API's render endpoint, set for the formats the browser
// can't display itself. It replaced the Microsoft embed viewer — see
// DocxReader for why that had to go.
export const SecureMedia = ({ url, kind, documentUrl, title = "" }) => {
    const resolved = kind || guessKind(url);
    if (!url) return null;

    const block = (e) => { e.preventDefault(); return false; };

    if (resolved === "video") {
        return (
            <div className="rounded-xl overflow-hidden bg-black w-full aspect-video">
                <video
                    src={url}
                    controls
                    playsInline
                    preload="metadata"
                    onContextMenu={block}
                    controlsList="nodownload noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    disableRemotePlayback
                    className="w-full h-full"
                >
                    Your browser can't play this video.
                </video>
            </div>
        );
    }

    if (resolved === "image") {
        return (
            <div className="rounded-xl overflow-hidden bg-black w-full aspect-video flex items-center justify-center">
                <img src={url} alt={title} draggable={false} onContextMenu={block}
                    className="w-full h-full object-contain pointer-events-none" />
            </div>
        );
    }

    if (resolved === "pdf") {
        return (
            <div className="rounded-xl bg-gray-100 p-2 sm:p-3 max-h-[80vh] overflow-y-auto">
                <Suspense fallback={<div className="p-10 text-center text-sm text-gray-400">Opening {title || "document"}…</div>}>
                    <PdfReader url={url} title={title} />
                </Suspense>
            </div>
        );
    }

    // Word / PowerPoint / Excel — the API renders these to HTML for us.
    if (resolved === "docx" || resolved === "office") {
        if (!documentUrl) {
            return (
                <div className="rounded-xl bg-gray-50 h-40 flex flex-col items-center justify-center text-center gap-2 p-6">
                    <FaFileAlt className="text-gray-400" size={26} />
                    <p className="text-sm text-gray-500">This document isn't available to view.</p>
                </div>
            );
        }
        return (
            <div className="rounded-xl bg-gray-100 p-2 sm:p-3 max-h-[80vh] overflow-y-auto">
                <Suspense fallback={<div className="p-10 text-center text-sm text-gray-400">Opening {title || "document"}…</div>}>
                    <DocxReader url={documentUrl} title={title} />
                </Suspense>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-gray-50 h-48 flex flex-col items-center justify-center text-center gap-2 p-6">
            <FaFileAlt className="text-gray-400" size={28} />
            <p className="text-sm text-gray-500">This file can't be previewed here.</p>
        </div>
    );
};

// Same mapping the API uses (utils/contentFiles.js) — kept here so an older
// cached response without `attachment_kind` still renders correctly.
export const guessKind = (url) => {
    const u = (url || "").toLowerCase().split("?")[0];
    if (/\.(mp4|mov|webm|m4v)$/.test(u)) return "video";
    if (/\.pdf$/.test(u)) return "pdf";
    if (/\.(jpg|jpeg|png|gif|webp)$/.test(u)) return "image";
    if (/\.docx$/.test(u)) return "docx";
    if (/\.(docx?|pptx?|xlsx?)$/.test(u)) return "office";
    return "file";
};
